import * as React from 'react';
import propz from 'propz';
import { Component } from 'react';
import { SchoolEvent } from '../../models/event';
import { AppUser } from 'Src/views/App/App';
import {
  isClubEvent,
  isHousesEvent,
  isInterSchoolsEvent,
  isNonTeamSportEvent,
  isResultsModePlaces,
  isResultsModeResults,
  sortByScore
} from 'Src/helpers/event/event';
import { convertPointsToStringWithoutDelimeter } from 'Src/helpers/event/schoolEventViewHelper';
import { convertPointsCricket, getCricketResults, isCricket } from 'Src/helpers/sport/cricket';
import { HTMLEditorQuillForMatchReport } from '../HTMLEditorQuill/HTMLEditorQuillForMatchReport';
import './SchoolEventMatchReportSummary.scss';

interface Props {
  user: AppUser;
  schoolEvent: SchoolEvent;
  matchReport: string;
  matchReportStatus?: 'draft' | 'published';
  onChangeMatchReport: (matchReport: any) => void;
  onEditMatchReportClick?: () => void;
  onSaveMatchReportClick?: () => void;
  onSaveMatchReportContent?: (matchReport: string, status: 'draft' | 'published') => void;
  isEditMatchReportActive: boolean;
}

interface State {
  isEditorOpen: boolean;
  draftReport: string;
  reportStatus: 'draft' | 'published';
}

interface ResultEntry {
  id: string;
  name: string;
  score: number;
  value: string;
  isWinner?: boolean;
  type: 'team' | 'school' | 'house' | 'student';
  avatar?: string;
  logo?: string;
  badgeName?: string;
  badgeLogo?: string;
  badgeColor?: string;
}

const WhistleIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
    <path d="M18 14l3 3M21 17l-2 4" />
  </svg>
);

const TrophyIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0z" />
    <path d="M7 6H4a2 2 0 0 0 2 4h1M17 6h3a2 2 0 0 1-2 4h-1" />
  </svg>
);

const DocumentIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6M9 13h6M9 17h4" />
  </svg>
);

const getInitials = (name: string) => {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');
};

const hasReportContent = (html: string) => {
  const text = String(html || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
  return text.length > 0;
};

const getImage = (item: any): string => {
  return (
    propz.get(item, ['avatar']) ||
    propz.get(item, ['pic']) ||
    propz.get(item, ['picUrl']) ||
    propz.get(item, ['logo']) ||
    propz.get(item, ['logoUrl']) ||
    ''
  );
};

const getScoreId = (score: any): string =>
  score.teamId || score.schoolId || score.houseId || `${score.userId}-${score.permissionId}`;

const getResultScore = (score: any): number => {
  const value = propz.get(score, ['richScore', 'result'], score.score);
  return Number(value || 0);
};

export class SchoolEventMatchReportSummary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isEditorOpen: false,
      draftReport: props.matchReport || '',
      reportStatus: props.matchReportStatus || (hasReportContent(props.matchReport) ? 'published' : 'draft')
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (
      (prevProps.matchReport !== this.props.matchReport ||
        prevProps.matchReportStatus !== this.props.matchReportStatus) &&
      !this.state.isEditorOpen
    ) {
      this.setState({
        draftReport: this.props.matchReport || '',
        reportStatus: this.props.matchReportStatus || (hasReportContent(this.props.matchReport) ? 'published' : 'draft')
      });
    }
  }

  getSchoolById = (schoolId: string) => {
    const { schoolEvent } = this.props;
    const schools = [
      propz.get(schoolEvent, ['inviterSchool']),
      ...(propz.get(schoolEvent, ['invitedSchools'], []) || [])
    ].filter(Boolean);

    return schools.find(school => school.id === schoolId);
  };

  getHouseById = (houseId: string) => {
    const houses = propz.get(this.props.schoolEvent, ['housesData'], []) || [];
    return houses.find(house => house.id === houseId);
  };

  formatScore = (score: number): string => {
    const { schoolEvent } = this.props;
    if (isCricket(schoolEvent)) {
      const cricketScore = convertPointsCricket(score);
      return `${cricketScore.runs}/${cricketScore.wickets}`;
    }

    return convertPointsToStringWithoutDelimeter(schoolEvent, score);
  };

  sortScores = (scores: any[]): any[] => {
    const { schoolEvent } = this.props;
    if (isResultsModePlaces(schoolEvent)) {
      return [...scores].sort((scoreA, scoreB) => Number(scoreA.score || 0) - Number(scoreB.score || 0));
    }

    return sortByScore(schoolEvent, scores);
  };

  sortIndividualScores = (scores: any[]): any[] => {
    const withRecordedResult = scores.filter(score => {
      const result = Number(score.score || 0);
      return result !== 0 && !Number.isNaN(result);
    });

    return this.sortScores(withRecordedResult);
  };

  getScoreEntries = (): ResultEntry[] => {
    const { schoolEvent } = this.props;
    const teamScores = propz.get(schoolEvent, ['results', 'teamScore'], []) || [];
    const schoolScores = propz.get(schoolEvent, ['results', 'schoolScore'], []) || [];
    const houseScores = propz.get(schoolEvent, ['results', 'houseScore'], []) || [];
    const teams = propz.get(schoolEvent, ['teamsData'], []) || [];

    if (teamScores.length > 0) {
      return this.sortScores(teamScores.map(score => ({ ...score, score: getResultScore(score) }))).map(score => {
        const team = teams.find(item => item.id === score.teamId);
        const school = team ? this.getSchoolById(team.schoolId) : undefined;
        const house = team ? this.getHouseById(team.houseId) : undefined;
        return {
          id: score.teamId,
          name: propz.get(team, ['name'], 'Team'),
          score: getResultScore(score),
          value: this.formatScore(getResultScore(score)),
          isWinner: score.isWinner,
          type: 'team',
          logo: getImage(school) || getImage(house),
          badgeName: propz.get(school, ['name']) || propz.get(house, ['name'])
        };
      });
    }

    if (schoolScores.length > 0) {
      return this.sortScores(schoolScores.map(score => ({ ...score, score: getResultScore(score) }))).map(score => {
        const school = this.getSchoolById(score.schoolId);
        return {
          id: score.schoolId,
          name: propz.get(school, ['name'], 'School'),
          score: getResultScore(score),
          value: this.formatScore(getResultScore(score)),
          isWinner: score.isWinner,
          type: 'school',
          logo: getImage(school)
        };
      });
    }

    if (houseScores.length > 0) {
      return this.sortScores(houseScores.map(score => ({ ...score, score: getResultScore(score) }))).map(score => {
        const house = this.getHouseById(score.houseId);
        return {
          id: score.houseId,
          name: propz.get(house, ['name'], 'House'),
          score: getResultScore(score),
          value: this.formatScore(getResultScore(score)),
          isWinner: score.isWinner,
          type: 'house',
          logo: getImage(house),
          badgeColor: propz.get(house, ['colors', 0], '')
        };
      });
    }

    return [];
  };

  getIndividualTopEntries = (): ResultEntry[] => {
    const { schoolEvent } = this.props;
    const individualScores = propz.get(schoolEvent, ['results', 'individualScore'], []) || [];
    const individualsData = propz.get(schoolEvent, ['individualsData'], []) || [];
    const eventPlayers = propz.get(schoolEvent, ['individuals'], []) || [];

    return this.sortIndividualScores(individualScores)
      .slice(0, 3)
      .map(score => {
        const playerData = individualsData.find(
          item => item.userId === score.userId && item.permissionId === score.permissionId
        );
        const eventPlayer = eventPlayers.find(
          item => item.userId === score.userId && item.permissionId === score.permissionId
        );
        const player = playerData || eventPlayer || score;
        const schoolId = propz.get(player, ['schoolId']) || propz.get(score, ['schoolId']);
        const houseId = propz.get(player, ['houseId']) || propz.get(score, ['houseId']);
        const school = schoolId ? this.getSchoolById(schoolId) : undefined;
        const house = houseId ? this.getHouseById(houseId) : undefined;
        const name = [propz.get(player, ['firstName'], ''), propz.get(player, ['lastName'], '')]
          .filter(Boolean)
          .join(' ');
        const showSchoolBadge = isInterSchoolsEvent(schoolEvent);
        const showHouseBadge = isHousesEvent(schoolEvent);

        return {
          id: getScoreId(score),
          name: name || 'Student',
          score: Number(score.score || 0),
          value: this.formatScore(Number(score.score || 0)),
          isWinner: score.isWinner,
          type: 'student',
          avatar: getImage(player),
          badgeName: showSchoolBadge
            ? propz.get(school, ['name'], '')
            : showHouseBadge
            ? propz.get(house, ['name'], '')
            : '',
          badgeLogo: showSchoolBadge ? getImage(school) : showHouseBadge ? getImage(house) : '',
          badgeColor: showHouseBadge ? propz.get(house, ['colors', 0], '') : ''
        };
      });
  };

  getOutcome = (scores: ResultEntry[]) => {
    if (scores.length !== 2) return null;
    const [first, second] = scores;
    if (first.score === second.score) return { label: 'Draw', className: 'eMatchReportOutcome--draw' };
    const winner = first.isWinner ? first : second.isWinner ? second : first.score > second.score ? first : second;
    return { label: `${winner.name} win`, className: 'eMatchReportOutcome--win' };
  };

  getScorerTeamName = (teamId: string): string => {
    const { schoolEvent } = this.props;
    const teams = [
      ...(propz.get(schoolEvent, ['embeddedTeams'], []) || []),
      ...(propz.get(schoolEvent, ['teamsData'], []) || [])
    ];
    const team = teams.find(item => String(item.id || item._id) === String(teamId));
    if (team && team.name) return team.name;
    const school = this.getSchoolById(teamId);
    return propz.get(school, ['name'], 'Team');
  };

  getScorerTiming = (scorer: any): string => {
    if (typeof scorer.minute === 'number') {
      return `${scorer.minute}'${typeof scorer.second === 'number' ? ` ${scorer.second}\"` : ''}`;
    }
    if (scorer.period === 'FIRST_HALF') return '1st half';
    if (scorer.period === 'SECOND_HALF') return '2nd half';
    if (scorer.period === 'EXTRA_TIME') return 'Extra time';
    if (scorer.period === 'SHOOTOUT') return 'Shoot-out';
    return '';
  };

  openEditor = () => {
    this.props.onEditMatchReportClick && this.props.onEditMatchReportClick();
    this.setState({
      isEditorOpen: true,
      draftReport: this.props.matchReport || '',
      reportStatus: 'draft'
    });
  };

  closeEditor = () => {
    this.setState({
      isEditorOpen: false,
      draftReport: this.props.matchReport || '',
      reportStatus: this.props.matchReportStatus || (hasReportContent(this.props.matchReport) ? 'published' : 'draft')
    });
  };

  saveReport = (status: 'draft' | 'published') => {
    const { draftReport } = this.state;

    if (this.props.onSaveMatchReportContent) {
      this.props.onSaveMatchReportContent(draftReport, status);
    } else {
      this.props.onChangeMatchReport(draftReport);
      this.props.onSaveMatchReportClick && this.props.onSaveMatchReportClick();
    }

    this.setState({
      isEditorOpen: false,
      reportStatus: status
    });
  };

  renderAvatar = (name: string, src?: string, className = '') => (
    <span className={`eMatchReportAvatar ${className}`}>
      {src ? <img src={src} alt="" /> : <span>{getInitials(name)}</span>}
    </span>
  );

  renderScoreBoard = () => {
    const { schoolEvent, user } = this.props;
    const isIndividualResult = isNonTeamSportEvent(schoolEvent);
    const isCricketResult = isCricket(schoolEvent);

    if (isIndividualResult) {
      return this.renderIndividualTopResults();
    }

    const scores = this.getScoreEntries();
    const isStandardScore = isResultsModeResults(schoolEvent) && scores.length === 2 && !isCricketResult;
    const outcome = isStandardScore ? this.getOutcome(scores) : null;

    if (scores.length === 0) {
      return (
        <section className="eMatchReportCard eMatchReportScoreEmpty">
          <span className="eMatchReportCard__icon">
            <TrophyIcon />
          </span>
          <div>
            <h3>Result</h3>
            <p>No score has been recorded yet.</p>
          </div>
        </section>
      );
    }

    if (!isStandardScore) {
      const title = isCricketResult ? getCricketResults(schoolEvent, user.activeSchoolId) : 'Result ranking';
      const subtitle = isResultsModePlaces(schoolEvent) ? 'Ordered by place' : 'Ordered by result';

      return (
        <section className="eMatchReportCard eMatchReportResultCard">
          <div className="eMatchReportResultHeader">
            <span className="eMatchReportCard__icon">
              <TrophyIcon />
            </span>
            <div>
              <h3>{title}</h3>
              <p>{subtitle}</p>
            </div>
          </div>
          <div className="eMatchReportResultList">
            {scores.map((entry, index) => this.renderResultRow(entry, index))}
          </div>
        </section>
      );
    }

    return (
      <section className="eMatchReportCard eMatchReportScoreCard">
        <div className="eMatchReportScoreBoard">
          {scores.map((entry, index) => (
            <React.Fragment key={entry.id || index}>
              <div className="eMatchReportScoreSide">
                {this.renderAvatar(entry.name, entry.logo)}
                <div className="eMatchReportScoreSide__name">{entry.name}</div>
              </div>
              {index === 0 && (
                <div className="eMatchReportScoreValue">
                  <span>{scores[0].value}</span>
                  <span className="eMatchReportScoreValue__dash">-</span>
                  <span>{scores[1] ? scores[1].value : 0}</span>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="eMatchReportScoreMeta">
          {outcome && <span className={`eMatchReportOutcome ${outcome.className}`}>{outcome.label}</span>}
          <span>{scores.length > 2 ? `${scores.length} result entries` : 'Current result'}</span>
        </div>
      </section>
    );
  };

  renderResultRow = (entry: ResultEntry, index: number) => (
    <div className="eMatchReportResultRow" key={entry.id || index}>
      <div className="eMatchReportResultRow__rank">{index + 1}</div>
      {this.renderAvatar(entry.name, entry.avatar || entry.logo, 'eMatchReportResultRow__avatar')}
      <div className="eMatchReportResultRow__body">
        <div className="eMatchReportResultRow__name">{entry.name}</div>
        {entry.badgeName && (
          <div className="eMatchReportResultBadge">
            {entry.badgeLogo ? (
              <img src={entry.badgeLogo} alt="" />
            ) : (
              <span style={entry.badgeColor ? { backgroundColor: entry.badgeColor } : undefined} />
            )}
            {entry.badgeName}
          </div>
        )}
      </div>
      <div className="eMatchReportResultRow__score">{entry.value}</div>
    </div>
  );

  renderIndividualTopResults = () => {
    const scores = this.getIndividualTopEntries();

    if (scores.length === 0) {
      return (
        <section className="eMatchReportCard eMatchReportScoreEmpty">
          <span className="eMatchReportCard__icon">
            <TrophyIcon />
          </span>
          <div>
            <h3>Top 3</h3>
            <p>No individual results have been recorded yet.</p>
          </div>
        </section>
      );
    }

    return (
      <section className="eMatchReportCard eMatchReportResultCard">
        <div className="eMatchReportResultHeader">
          <span className="eMatchReportCard__icon">
            <TrophyIcon />
          </span>
          <div>
            <h3>Top 3</h3>
            <p>Best individual results for this event.</p>
          </div>
        </div>
        <div className="eMatchReportResultList">{scores.map((entry, index) => this.renderResultRow(entry, index))}</div>
      </section>
    );
  };

  renderEditorModal = (title: string) => {
    const { isEditorOpen, draftReport } = this.state;

    if (!isEditorOpen) return null;

    return (
      <div className="eMatchReportModalOverlay" role="presentation">
        <div className="eMatchReportModal" role="dialog" aria-modal="true" aria-label={title}>
          <div className="eMatchReportModal__header">
            <div>
              <h3>{title}</h3>
              <p>Draft the report, then publish it when it is ready.</p>
            </div>
            <button type="button" className="eMatchReportModal__close" aria-label="Close" onClick={this.closeEditor}>
              ×
            </button>
          </div>
          <div className="eMatchReportModal__body">
            <div className="eMatchReportEditor">
              <HTMLEditorQuillForMatchReport
                user={this.props.user}
                onTextMatchReportChange={nextMatchReport => {
                  this.setState({ draftReport: nextMatchReport, reportStatus: 'draft' });
                }}
                value={draftReport}
                isEnabled
                isEnabledEditorActiveToolbar
                placeholder="Write the match report"
              />
            </div>
          </div>
          <div className="eMatchReportModal__footer">
            <button type="button" className="sit-btn sit-btn--secondary sit-btn--sm" onClick={this.closeEditor}>
              Cancel
            </button>
            <button
              type="button"
              className="sit-btn sit-btn--secondary sit-btn--sm"
              onClick={() => this.saveReport('draft')}
            >
              Save draft
            </button>
            <button
              type="button"
              className="sit-btn sit-btn--primary sit-btn--sm"
              onClick={() => this.saveReport('published')}
            >
              Publish report
            </button>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { matchReport, schoolEvent } = this.props;
    const { reportStatus } = this.state;
    const title = isClubEvent(schoolEvent) ? 'Leader/Coach notes' : 'Match report';
    const hasContent = hasReportContent(matchReport);
    const isPublished = reportStatus === 'published' && hasContent;
    const scorers = propz.get(schoolEvent, ['matchResult', 'scorers'], []) || [];

    return (
      <div className="eMatchReport">
        <div className="eMatchReportHeader">
          <div className="eMatchReportHeader__copy">
            <span className="eMatchReportHeader__icon">
              <WhistleIcon />
            </span>
            <div>
              <h2 className="eMatchReportHeader__title">{title}</h2>
              <div className="eMatchReportHeader__subtitle">
                Record the result narrative and shareable match summary.
              </div>
            </div>
          </div>
          <div className="eMatchReportHeader__actions">
            <span className={isPublished ? 'eMatchReportStatus eMatchReportStatus--ready' : 'eMatchReportStatus'}>
              {isPublished ? 'Published' : 'Draft'}
            </span>
            <button type="button" className="sit-btn sit-btn--primary sit-btn--sm" onClick={this.openEditor}>
              {hasContent ? 'Edit draft' : 'Write report'}
            </button>
          </div>
        </div>

        <div className="eMatchReportLayout">
          <div className="eMatchReportLayout__main">
            {this.renderScoreBoard()}

            <section className="eMatchReportCard eMatchReportEditorCard">
              <div className="eMatchReportCard__head">
                <span className="eMatchReportCard__icon">
                  <DocumentIcon />
                </span>
                <div>
                  <h3>{title}</h3>
                  <p>Shared in reports and public-facing summaries where enabled.</p>
                </div>
              </div>

              {hasContent ? (
                <div className="eMatchReportContent" dangerouslySetInnerHTML={{ __html: matchReport }} />
              ) : (
                <div className="eMatchReportEmpty">No report has been written yet.</div>
              )}
            </section>
          </div>

          <aside className="eMatchReportSide">
            <div className="eMatchReportSideCard">
              <div className="eMatchReportSideCard__label">Status</div>
              <div className={isPublished ? 'eMatchReportStatus eMatchReportStatus--ready' : 'eMatchReportStatus'}>
                {isPublished ? 'Published' : 'Draft'}
              </div>
            </div>
            <div className="eMatchReportSideCard">
              <div className="eMatchReportSideCard__label">Scorers</div>
              {scorers.length > 0 ? (
                <div className="eMatchReportScorers">
                  {scorers.map((scorer, index) => {
                    const timing = this.getScorerTiming(scorer);
                    return (
                      <div className="eMatchReportScorer" key={scorer.id || `${scorer.teamId}-${index}`}>
                        <span className="eMatchReportScorer__minute">{timing || 'Goal'}</span>
                        <span className="eMatchReportScorer__copy">
                          <strong>{scorer.playerNameSnapshot || 'Scorer'}</strong>
                          <small>{this.getScorerTeamName(scorer.teamId)}{scorer.ownGoal ? ' · own goal' : ''}</small>
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="eMatchReportSideCard__text">No scorers have been recorded for this event.</div>
              )}
            </div>
          </aside>
        </div>
        {this.renderEditorModal(title)}
      </div>
    );
  }
}
